/**
 * Admin Console — Main Page
 * Tabbed: Clients | Document & Line Item Types | Plans | API Key
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Edit05, Lock01, Plus, SearchLg, Tag01 } from "@untitledui/icons";
import { useNavigate } from "react-router-dom";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Tab, TabList, TabPanel, Tabs } from "@/components/application/tabs/tabs";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { Toggle } from "@/components/base/toggle/toggle";
import { useAdminApi } from "@/services/api/admin-api";
import { useHealthApi } from "@/services/api/health-api";
import type {
    AcomDefaultsDto,
    AdminClientListParams,
    ClientAdminListItemDto,
    CreateClientRequestDto,
    CreateDocumentTypeRequestDto,
    CreateLineItemTypeRequestDto,
    CreatePlanRequestDto,
    DocumentTypeAdminListItemDto,
    LineItemTypeAdminListItemDto,
    PlanAdminListItemDto,
    PlanEntitlementAdminListItemDto,
    UpdateDocumentTypeRequestDto,
    UpdateLineItemTypeRequestDto,
    UpdatePlanEntitlementRequestDto,
    UpdatePlanRequestDto,
} from "@/types/api/admin";
import type { BackendVersionDto } from "@/types/api/health";
import { AdminShell } from "./AdminShell";
import { MailboxWorkerKeyTab } from "./components/MailboxWorkerKeyTab";

// =============================================================================
// CONSTANTS
// =============================================================================

const PAGE_SIZE = 25;

const TABS = [
    { id: "clients", label: "Clients" },
    { id: "document-types", label: "Document & Line Item Types" },
    { id: "plans", label: "Plans" },
    { id: "api-key", label: "API Key" },
];

// =============================================================================
// HELPERS
// =============================================================================

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

// =============================================================================
// CREATE CLIENT MODAL
// =============================================================================

interface CreateClientModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
}

function CreateClientModal({ isOpen, onClose, onCreated }: CreateClientModalProps) {
    const adminApi = useAdminApi();
    const [code, setCode] = useState("");
    const [name, setName] = useState("");
    const [orgCode, setOrgCode] = useState("");
    const [orgName, setOrgName] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [defaults, setDefaults] = useState<AcomDefaultsDto | null>(null);

    // Load ACOM defaults when modal opens
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        adminApi
            .getAcomDefaults()
            .then((d) => {
                if (!cancelled) setDefaults(d);
            })
            .catch(() => {
                /* best-effort */
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen, adminApi]);

    const handleCreate = useCallback(async () => {
        if (!code.trim() || !name.trim() || !orgCode.trim() || !orgName.trim()) {
            setError("Code, name, organization code and organization name are required.");
            return;
        }

        setIsSaving(true);
        setError(null);
        try {
            // Build settings from ACOM defaults if available
            const settings: Record<string, unknown> = {};
            if (defaults?.ocrPrompt) settings["OcrPrompt"] = defaults.ocrPrompt;
            if (defaults?.classificationPrompt) settings["ClassificationPrompt"] = defaults.classificationPrompt;
            if (defaults?.classificationSchema) settings["ClassificationSchema"] = defaults.classificationSchema;
            if (defaults?.parsePrompt) settings["ParsePrompt"] = defaults.parsePrompt;

            const dto: CreateClientRequestDto = {
                code: code.trim().toUpperCase(),
                name: name.trim(),
                organizationCode: orgCode.trim(),
                organizationName: orgName.trim(),
                isActive,
                settings: Object.keys(settings).length > 0 ? settings : null,
            };
            await adminApi.createClient(dto);
            setCode("");
            setName("");
            setOrgCode("");
            setOrgName("");
            setIsActive(true);
            setDefaults(null);
            onCreated();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to create client.");
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, code, name, orgCode, orgName, isActive, defaults, onCreated, onClose]);

    if (!isOpen) return null;

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && onClose()} isDismissable>
            <Modal className="max-w-md">
                <Dialog>
                    <div className="flex w-full flex-col gap-5 rounded-xl border border-secondary bg-primary p-6 shadow-xl">
                        <div>
                            <h2 className="text-lg font-semibold text-primary">Create Client</h2>
                            <p className="mt-1 text-sm text-tertiary">
                                A default organization (using the values below) and Generic plan will be created automatically.
                                {defaults && " Default prompts and schema are pre-populated from the ACOM template."}
                            </p>
                        </div>

                        <div className="flex flex-col gap-4">
                            <Input label="Code" placeholder="e.g. ACME" value={code} onChange={setCode} isRequired />
                            <Input label="Name" placeholder="e.g. Acme Corporation" value={name} onChange={setName} isRequired />
                            <Input label="Organization Code" placeholder="e.g. STORE-001" value={orgCode} onChange={setOrgCode} isRequired />
                            <Input label="Organization Name" placeholder="Organization name" value={orgName} onChange={setOrgName} isRequired />
                            <Toggle label="Active" isSelected={isActive} onChange={setIsActive} size="sm" />
                        </div>

                        {error && <p className="text-sm text-error-primary">{error}</p>}

                        <div className="flex justify-end gap-3">
                            <Button color="secondary" size="md" onClick={onClose} isDisabled={isSaving}>
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                size="md"
                                onClick={handleCreate}
                                isDisabled={isSaving || !code.trim() || !name.trim() || !orgCode.trim() || !orgName.trim()}
                            >
                                {isSaving ? "Creating..." : "Create Client"}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}

// =============================================================================
// CLIENTS SECTION
// =============================================================================

interface ClientsSectionProps {
    onCreateOpen: () => void;
}

function ClientsSection({ onCreateOpen }: ClientsSectionProps) {
    const navigate = useNavigate();
    const adminApi = useAdminApi();

    const [clients, setClients] = useState<ClientAdminListItemDto[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [activeOnly, setActiveOnly] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);

    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setCurrentPage(0);
        }, 300);
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, [searchQuery]);

    const fetchClients = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params: AdminClientListParams = {
                pageSize: PAGE_SIZE,
                skip: currentPage * PAGE_SIZE,
            };
            if (debouncedSearch) params.search = debouncedSearch;
            if (activeOnly) params.isActive = true;

            const response = await adminApi.listClients(params);

            // Pin ACOM client first
            const sorted = [...response.data].sort((a, b) => {
                if (a.code === "ACOM") return -1;
                if (b.code === "ACOM") return 1;
                return 0;
            });
            setClients(sorted);
            setTotalCount(response.total);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load clients.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, currentPage, debouncedSearch, activeOnly]);

    useEffect(() => {
        fetchClients();
    }, [fetchClients]);

    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    const canPrevPage = currentPage > 0;
    const canNextPage = currentPage < totalPages - 1;

    return (
        <>
            <div className="mb-4 flex items-center gap-4">
                <div className="w-80">
                    <Input placeholder="Search by code or name..." icon={SearchLg} value={searchQuery} onChange={setSearchQuery} size="sm" />
                </div>
                <Toggle
                    label="Active only"
                    isSelected={activeOnly}
                    onChange={(val) => {
                        setActiveOnly(val);
                        setCurrentPage(0);
                    }}
                    size="sm"
                />
                <Button size="sm" color="primary" iconLeading={Plus} onClick={onCreateOpen} className="ml-auto">
                    Create Client
                </Button>
                {totalCount > 0 && (
                    <span className="text-sm text-tertiary">
                        {totalCount} client{totalCount !== 1 ? "s" : ""}
                    </span>
                )}
            </div>

            {isLoading && clients.length === 0 ? (
                <div className="flex items-center justify-center py-20">
                    <LoadingIndicator size="lg" label="Loading clients..." />
                </div>
            ) : error ? (
                <div className="flex items-center justify-center py-20">
                    <p className="text-sm text-error-primary">{error}</p>
                </div>
            ) : clients.length === 0 ? (
                <EmptyState size="lg">
                    <EmptyState.Header>
                        <EmptyState.FeaturedIcon icon={SearchLg} color="brand" />
                    </EmptyState.Header>
                    <EmptyState.Content>
                        <EmptyState.Title>{debouncedSearch || activeOnly ? "No clients match your filters" : "No clients yet"}</EmptyState.Title>
                        <EmptyState.Description>
                            {debouncedSearch || activeOnly ? "Try adjusting your search or filter criteria." : "Create your first client to get started."}
                        </EmptyState.Description>
                    </EmptyState.Content>
                    {!debouncedSearch && !activeOnly && (
                        <EmptyState.Footer>
                            <Button size="md" color="primary" iconLeading={Plus} onClick={onCreateOpen}>
                                Create Client
                            </Button>
                        </EmptyState.Footer>
                    )}
                </EmptyState>
            ) : (
                <>
                    <div className="overflow-hidden rounded-xl bg-primary shadow-xs ring-1 ring-secondary">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-secondary bg-secondary_subtle">
                                    <th className="px-6 py-3 text-left text-xs font-medium text-tertiary">Code</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-tertiary">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-tertiary">Status</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-tertiary">Orgs</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-tertiary">Users</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-tertiary">Updated</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clients.map((client) => (
                                    <tr
                                        key={client.id}
                                        onClick={() => navigate(`/admin/clients/${client.id}`)}
                                        className="cursor-pointer border-b border-secondary transition-colors last:border-0 hover:bg-primary_hover"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-sm font-medium text-primary">{client.code}</span>
                                                {client.code === "ACOM" && (
                                                    <Badge color="purple" size="sm" type="pill-color">
                                                        Platform
                                                    </Badge>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-primary">{client.name}</td>
                                        <td className="px-6 py-4">
                                            <Badge color={client.isActive ? "success" : "gray"} size="sm" type="pill-color">
                                                {client.isActive ? "Active" : "Inactive"}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-right text-sm text-tertiary">{client.organizationCount}</td>
                                        <td className="px-6 py-4 text-right text-sm text-tertiary">{client.userCount}</td>
                                        <td className="px-6 py-4 text-sm text-tertiary">{formatDate(client.updatedAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="mt-4 flex items-center justify-between">
                            <span className="text-sm text-tertiary">
                                Page {currentPage + 1} of {totalPages}
                            </span>
                            <div className="flex gap-2">
                                <Button color="secondary" size="sm" isDisabled={!canPrevPage} onClick={() => setCurrentPage((p) => p - 1)}>
                                    Previous
                                </Button>
                                <Button color="secondary" size="sm" isDisabled={!canNextPage} onClick={() => setCurrentPage((p) => p + 1)}>
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
}

// =============================================================================
// DOCUMENT TYPES SECTION
// =============================================================================

function DocumentTypesSection() {
    const adminApi = useAdminApi();
    const [docTypes, setDocTypes] = useState<DocumentTypeAdminListItemDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // New row form
    const [showNewRow, setShowNewRow] = useState(false);
    const [newCode, setNewCode] = useState("");
    const [newName, setNewName] = useState("");
    const [newDesc, setNewDesc] = useState("");
    const [isCreating, setIsCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    // Edit state
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editName, setEditName] = useState("");
    const [editDesc, setEditDesc] = useState("");
    const [editActive, setEditActive] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const fetchDocTypes = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await adminApi.listDocumentTypes();
            setDocTypes(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load document types.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi]);

    useEffect(() => {
        fetchDocTypes();
    }, [fetchDocTypes]);

    const startEdit = useCallback((dt: DocumentTypeAdminListItemDto) => {
        setEditingId(dt.id);
        setEditName(dt.name);
        setEditDesc(dt.description ?? "");
        setEditActive(dt.isActive);
        setSaveError(null);
    }, []);

    const cancelEdit = useCallback(() => {
        setEditingId(null);
        setSaveError(null);
    }, []);

    const saveEdit = useCallback(
        async (dt: DocumentTypeAdminListItemDto) => {
            setIsSaving(true);
            setSaveError(null);
            try {
                const dto: UpdateDocumentTypeRequestDto = {
                    name: editName.trim(),
                    description: editDesc.trim() || null,
                    isActive: editActive,
                };
                await adminApi.updateDocumentType(dt.id, dto);
                setEditingId(null);
                await fetchDocTypes();
            } catch (err) {
                setSaveError(err instanceof Error ? err.message : "Failed to save.");
            } finally {
                setIsSaving(false);
            }
        },
        [adminApi, editName, editDesc, editActive, fetchDocTypes],
    );

    const handleCreate = useCallback(async () => {
        if (!newCode.trim() || !newName.trim()) {
            setCreateError("Code and name are required.");
            return;
        }
        setIsCreating(true);
        setCreateError(null);
        try {
            const dto: CreateDocumentTypeRequestDto = {
                code: newCode.trim().toUpperCase(),
                name: newName.trim(),
                description: newDesc.trim() || null,
            };
            await adminApi.createDocumentType(dto);
            setNewCode("");
            setNewName("");
            setNewDesc("");
            setShowNewRow(false);
            await fetchDocTypes();
        } catch (err) {
            setCreateError(err instanceof Error ? err.message : "Failed to create.");
        } finally {
            setIsCreating(false);
        }
    }, [adminApi, newCode, newName, newDesc, fetchDocTypes]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <LoadingIndicator size="lg" label="Loading document types..." />
            </div>
        );
    }

    if (error) {
        return <p className="py-6 text-sm text-error-primary">{error}</p>;
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <p className="text-sm text-tertiary">Click any row to edit. System types and &quot;unknown&quot; are read-only.</p>
                <Button
                    size="sm"
                    color="primary"
                    iconLeading={Plus}
                    onClick={() => {
                        setShowNewRow(true);
                        setEditingId(null);
                    }}
                >
                    New Document Type
                </Button>
            </div>

            <div className="overflow-hidden rounded-xl bg-primary shadow-xs ring-1 ring-secondary">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-secondary bg-secondary_subtle">
                            <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Code</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Name</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Description</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">System</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Active</th>
                        </tr>
                    </thead>
                    <tbody>
                        {docTypes.map((dt) => {
                            const isLocked = dt.isSystem || dt.code.toLowerCase() === "unknown";
                            const isEditing = editingId === dt.id;
                            return isEditing ? (
                                <tr key={dt.id} className="border-b border-secondary bg-secondary_subtle last:border-0">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            {isLocked && <Lock01 className="h-3.5 w-3.5 flex-shrink-0 text-tertiary" />}
                                            <span className="font-mono text-sm text-tertiary">{dt.code}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <Input size="sm" value={editName} onChange={setEditName} placeholder="Name" isDisabled={isLocked} />
                                    </td>
                                    <td className="px-4 py-3">
                                        <Input size="sm" value={editDesc} onChange={setEditDesc} placeholder="Description (optional)" isDisabled={isLocked} />
                                    </td>
                                    <td className="px-4 py-3">
                                        {dt.isSystem ? (
                                            <Badge color="gray" size="sm" type="pill-color">
                                                System
                                            </Badge>
                                        ) : (
                                            <span className="text-sm text-tertiary">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-col gap-2">
                                            <Toggle size="sm" isSelected={editActive} onChange={setEditActive} isDisabled={isLocked} />
                                            <div className="flex items-center gap-2">
                                                {saveError && <span className="text-xs text-error-primary">{saveError}</span>}
                                                <Button size="sm" color="secondary" onClick={cancelEdit} isDisabled={isSaving}>
                                                    Cancel
                                                </Button>
                                                {!isLocked && (
                                                    <Button size="sm" color="primary" onClick={() => saveEdit(dt)} isDisabled={isSaving || !editName.trim()}>
                                                        {isSaving ? "Saving..." : "Save"}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                <tr
                                    key={dt.id}
                                    className="cursor-pointer border-b border-secondary transition-colors last:border-0 hover:bg-primary_hover"
                                    onClick={() => startEdit(dt)}
                                >
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            {isLocked && <Lock01 className="h-3.5 w-3.5 flex-shrink-0 text-tertiary" />}
                                            <span className="font-mono text-sm font-medium text-primary">{dt.code}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-primary">{dt.name}</td>
                                    <td className="px-4 py-3 text-sm text-tertiary">{dt.description ?? "—"}</td>
                                    <td className="px-4 py-3">
                                        {dt.isSystem ? (
                                            <Badge color="gray" size="sm" type="pill-color">
                                                System
                                            </Badge>
                                        ) : (
                                            <span className="text-sm text-tertiary">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <Badge color={dt.isActive ? "success" : "gray"} size="sm" type="pill-color">
                                            {dt.isActive ? "Active" : "Inactive"}
                                        </Badge>
                                    </td>
                                </tr>
                            );
                        })}

                        {/* New row */}
                        {showNewRow && (
                            <tr className="border-b border-secondary bg-secondary_subtle last:border-0">
                                <td className="px-4 py-3">
                                    <Input size="sm" value={newCode} onChange={setNewCode} placeholder="CODE" />
                                </td>
                                <td className="px-4 py-3">
                                    <Input size="sm" value={newName} onChange={setNewName} placeholder="Name" />
                                </td>
                                <td className="px-4 py-3">
                                    <Input size="sm" value={newDesc} onChange={setNewDesc} placeholder="Description (optional)" />
                                </td>
                                <td className="px-4 py-3">
                                    <span className="text-sm text-tertiary">—</span>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex flex-col gap-2">
                                        <span className="text-sm text-tertiary">Active</span>
                                        <div className="flex items-center gap-2">
                                            {createError && <span className="text-xs text-error-primary">{createError}</span>}
                                            <Button
                                                size="sm"
                                                color="secondary"
                                                onClick={() => {
                                                    setShowNewRow(false);
                                                    setCreateError(null);
                                                }}
                                                isDisabled={isCreating}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                size="sm"
                                                color="primary"
                                                onClick={handleCreate}
                                                isDisabled={isCreating || !newCode.trim() || !newName.trim()}
                                            >
                                                {isCreating ? "Creating..." : "Create"}
                                            </Button>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// =============================================================================
// LINE ITEM TYPES SECTION
// =============================================================================

function LineItemTypesSection() {
    const adminApi = useAdminApi();
    const [lineItemTypes, setLineItemTypes] = useState<LineItemTypeAdminListItemDto[]>([]);
    const [docTypes, setDocTypes] = useState<DocumentTypeAdminListItemDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // New row form
    const [showNewRow, setShowNewRow] = useState(false);
    const [newCode, setNewCode] = useState("");
    const [newName, setNewName] = useState("");
    const [newDesc, setNewDesc] = useState("");
    const [newDocTypeId, setNewDocTypeId] = useState("");
    const [isCreating, setIsCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    // Edit state
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editName, setEditName] = useState("");
    const [editDesc, setEditDesc] = useState("");
    const [editActive, setEditActive] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [lits, dts] = await Promise.all([adminApi.listLineItemTypes(), adminApi.listDocumentTypes()]);
            setLineItemTypes(lits);
            setDocTypes(dts);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load line item types.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const docTypeLabel = useCallback(
        (documentTypeId: number): string => {
            const dt = docTypes.find((d) => d.id === documentTypeId);
            return dt ? dt.code : `ID ${documentTypeId}`;
        },
        [docTypes],
    );

    const docTypeOptions = docTypes.map((dt) => ({ id: String(dt.id), label: dt.code, supportingText: dt.name }));

    const startEdit = useCallback((lit: LineItemTypeAdminListItemDto) => {
        setEditingId(lit.id);
        setEditName(lit.name);
        setEditDesc(lit.description ?? "");
        setEditActive(lit.isActive);
        setSaveError(null);
    }, []);

    const cancelEdit = useCallback(() => {
        setEditingId(null);
        setSaveError(null);
    }, []);

    const saveEdit = useCallback(
        async (lit: LineItemTypeAdminListItemDto) => {
            setIsSaving(true);
            setSaveError(null);
            try {
                const dto: UpdateLineItemTypeRequestDto = {
                    name: editName.trim(),
                    description: editDesc.trim() || null,
                    isActive: editActive,
                };
                await adminApi.updateLineItemType(lit.id, dto);
                setEditingId(null);
                await fetchData();
            } catch (err) {
                setSaveError(err instanceof Error ? err.message : "Failed to save.");
            } finally {
                setIsSaving(false);
            }
        },
        [adminApi, editName, editDesc, editActive, fetchData],
    );

    const handleCreate = useCallback(async () => {
        if (!newCode.trim() || !newName.trim() || !newDocTypeId) {
            setCreateError("Code, name, and document type are required.");
            return;
        }
        setIsCreating(true);
        setCreateError(null);
        try {
            const dto: CreateLineItemTypeRequestDto = {
                code: newCode.trim().toUpperCase(),
                name: newName.trim(),
                description: newDesc.trim() || null,
                documentTypeId: Number(newDocTypeId),
            };
            await adminApi.createLineItemType(dto);
            setNewCode("");
            setNewName("");
            setNewDesc("");
            setNewDocTypeId("");
            setShowNewRow(false);
            await fetchData();
        } catch (err) {
            setCreateError(err instanceof Error ? err.message : "Failed to create.");
        } finally {
            setIsCreating(false);
        }
    }, [adminApi, newCode, newName, newDesc, newDocTypeId, fetchData]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <LoadingIndicator size="lg" label="Loading line item types..." />
            </div>
        );
    }

    if (error) {
        return <p className="py-6 text-sm text-error-primary">{error}</p>;
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <p className="text-sm text-tertiary">Click any row to edit. System types are read-only. Each type is linked to a document type.</p>
                <Button
                    size="sm"
                    color="primary"
                    iconLeading={Plus}
                    onClick={() => {
                        setShowNewRow(true);
                        setEditingId(null);
                    }}
                >
                    New Line Item Type
                </Button>
            </div>

            <div className="overflow-hidden rounded-xl bg-primary shadow-xs ring-1 ring-secondary">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-secondary bg-secondary_subtle">
                            <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Code</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Name</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Description</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Document Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">System</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Active</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lineItemTypes.map((lit) => {
                            const isLocked = lit.isSystem;
                            const isEditing = editingId === lit.id;
                            return isEditing ? (
                                <tr key={lit.id} className="border-b border-secondary bg-secondary_subtle last:border-0">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            {isLocked && <Lock01 className="h-3.5 w-3.5 flex-shrink-0 text-tertiary" />}
                                            <span className="font-mono text-sm text-tertiary">{lit.code}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <Input size="sm" value={editName} onChange={setEditName} placeholder="Name" isDisabled={isLocked} />
                                    </td>
                                    <td className="px-4 py-3">
                                        <Input size="sm" value={editDesc} onChange={setEditDesc} placeholder="Description (optional)" isDisabled={isLocked} />
                                    </td>
                                    <td className="px-4 py-3 text-sm text-tertiary">{docTypeLabel(lit.documentTypeId)}</td>
                                    <td className="px-4 py-3">
                                        {lit.isSystem ? (
                                            <Badge color="gray" size="sm" type="pill-color">
                                                System
                                            </Badge>
                                        ) : (
                                            <span className="text-sm text-tertiary">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-col gap-2">
                                            <Toggle size="sm" isSelected={editActive} onChange={setEditActive} isDisabled={isLocked} />
                                            <div className="flex items-center gap-2">
                                                {saveError && <span className="text-xs text-error-primary">{saveError}</span>}
                                                <Button size="sm" color="secondary" onClick={cancelEdit} isDisabled={isSaving}>
                                                    Cancel
                                                </Button>
                                                {!isLocked && (
                                                    <Button size="sm" color="primary" onClick={() => saveEdit(lit)} isDisabled={isSaving || !editName.trim()}>
                                                        {isSaving ? "Saving..." : "Save"}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                <tr
                                    key={lit.id}
                                    className="cursor-pointer border-b border-secondary transition-colors last:border-0 hover:bg-primary_hover"
                                    onClick={() => startEdit(lit)}
                                >
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            {isLocked && <Lock01 className="h-3.5 w-3.5 flex-shrink-0 text-tertiary" />}
                                            <span className="font-mono text-sm font-medium text-primary">{lit.code}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-primary">{lit.name}</td>
                                    <td className="px-4 py-3 text-sm text-tertiary">{lit.description ?? "—"}</td>
                                    <td className="px-4 py-3 text-sm text-tertiary">{docTypeLabel(lit.documentTypeId)}</td>
                                    <td className="px-4 py-3">
                                        {lit.isSystem ? (
                                            <Badge color="gray" size="sm" type="pill-color">
                                                System
                                            </Badge>
                                        ) : (
                                            <span className="text-sm text-tertiary">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <Badge color={lit.isActive ? "success" : "gray"} size="sm" type="pill-color">
                                            {lit.isActive ? "Active" : "Inactive"}
                                        </Badge>
                                    </td>
                                </tr>
                            );
                        })}

                        {/* New row */}
                        {showNewRow && (
                            <tr className="border-b border-secondary bg-secondary_subtle last:border-0">
                                <td className="px-4 py-3">
                                    <Input size="sm" value={newCode} onChange={setNewCode} placeholder="CODE" />
                                </td>
                                <td className="px-4 py-3">
                                    <Input size="sm" value={newName} onChange={setNewName} placeholder="Name" />
                                </td>
                                <td className="px-4 py-3">
                                    <Input size="sm" value={newDesc} onChange={setNewDesc} placeholder="Description (optional)" />
                                </td>
                                <td className="px-4 py-3">
                                    <Select
                                        aria-label="Document Type"
                                        size="sm"
                                        items={docTypeOptions}
                                        selectedKey={newDocTypeId || null}
                                        onSelectionChange={(key) => setNewDocTypeId(String(key ?? ""))}
                                        placeholder="Select..."
                                        popoverClassName="w-auto min-w-72"
                                    >
                                        {(item) => <SelectItem id={item.id} label={item.label} supportingText={item.supportingText} />}
                                    </Select>
                                </td>
                                <td className="px-4 py-3">
                                    <span className="text-sm text-tertiary">—</span>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex flex-col gap-2">
                                        <span className="text-sm text-tertiary">Active</span>
                                        <div className="flex items-center gap-2">
                                            {createError && <span className="text-xs text-error-primary">{createError}</span>}
                                            <Button
                                                size="sm"
                                                color="secondary"
                                                onClick={() => {
                                                    setShowNewRow(false);
                                                    setCreateError(null);
                                                }}
                                                isDisabled={isCreating}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                size="sm"
                                                color="primary"
                                                onClick={handleCreate}
                                                isDisabled={isCreating || !newCode.trim() || !newName.trim() || !newDocTypeId}
                                            >
                                                {isCreating ? "Creating..." : "Create"}
                                            </Button>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// =============================================================================
// PLANS SECTION
// =============================================================================

function PlansSection() {
    const adminApi = useAdminApi();
    const [plans, setPlans] = useState<PlanAdminListItemDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);

    // Entitlements panel
    const [entitlements, setEntitlements] = useState<PlanEntitlementAdminListItemDto[]>([]);
    const [entitlementsLoading, setEntitlementsLoading] = useState(false);

    // New plan form
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newCode, setNewCode] = useState("");
    const [newName, setNewName] = useState("");
    const [newDesc, setNewDesc] = useState("");
    const [isCreating, setIsCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    // Edit plan
    const [editingPlanId, setEditingPlanId] = useState<number | null>(null);
    const [editPlanName, setEditPlanName] = useState("");
    const [editPlanDesc, setEditPlanDesc] = useState("");
    const [editPlanActive, setEditPlanActive] = useState(true);
    const [isSavingPlan, setIsSavingPlan] = useState(false);
    const [savePlanError, setSavePlanError] = useState<string | null>(null);

    const fetchPlans = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await adminApi.listPlans();
            setPlans(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load plans.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi]);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    const fetchEntitlements = useCallback(
        async (planId: number) => {
            setEntitlementsLoading(true);
            try {
                const data = await adminApi.getPlanEntitlements(planId);
                setEntitlements(data);
            } catch {
                setEntitlements([]);
            } finally {
                setEntitlementsLoading(false);
            }
        },
        [adminApi],
    );

    const handleSelectPlan = useCallback(
        (planId: number) => {
            setSelectedPlanId(planId);
            setEditingPlanId(null);
            fetchEntitlements(planId);
        },
        [fetchEntitlements],
    );

    const handleCreatePlan = useCallback(async () => {
        if (!newCode.trim() || !newName.trim()) {
            setCreateError("Code and name are required.");
            return;
        }
        setIsCreating(true);
        setCreateError(null);
        try {
            const dto: CreatePlanRequestDto = {
                code: newCode.trim().toUpperCase(),
                name: newName.trim(),
                description: newDesc.trim() || null,
                isActive: true,
            };
            await adminApi.createPlan(dto);
            setNewCode("");
            setNewName("");
            setNewDesc("");
            setShowCreateForm(false);
            await fetchPlans();
        } catch (err) {
            setCreateError(err instanceof Error ? err.message : "Failed to create plan.");
        } finally {
            setIsCreating(false);
        }
    }, [adminApi, newCode, newName, newDesc, fetchPlans]);

    const startEditPlan = useCallback((plan: PlanAdminListItemDto) => {
        setEditingPlanId(plan.id);
        setEditPlanName(plan.name);
        setEditPlanDesc(plan.description ?? "");
        setEditPlanActive(plan.isActive);
        setSavePlanError(null);
    }, []);

    const savePlan = useCallback(
        async (plan: PlanAdminListItemDto) => {
            setIsSavingPlan(true);
            setSavePlanError(null);
            try {
                const dto: UpdatePlanRequestDto = {
                    name: editPlanName.trim(),
                    description: editPlanDesc.trim() || null,
                    isActive: editPlanActive,
                };
                await adminApi.updatePlan(plan.id, dto);
                setEditingPlanId(null);
                await fetchPlans();
            } catch (err) {
                setSavePlanError(err instanceof Error ? err.message : "Failed to save plan.");
            } finally {
                setIsSavingPlan(false);
            }
        },
        [adminApi, editPlanName, editPlanDesc, editPlanActive, fetchPlans],
    );

    const handleEntitlementToggle = useCallback(
        async (planId: number, entitlement: PlanEntitlementAdminListItemDto, isEnabled: boolean) => {
            const dto: UpdatePlanEntitlementRequestDto = {
                isEnabled,
                limitValue: entitlement.limitValue,
            };
            try {
                await adminApi.updatePlanEntitlement(planId, entitlement.entitlementId, dto);
                await fetchEntitlements(planId);
            } catch {
                // revert on error
                await fetchEntitlements(planId);
            }
        },
        [adminApi, fetchEntitlements],
    );

    const handleEntitlementLimit = useCallback(
        async (planId: number, entitlement: PlanEntitlementAdminListItemDto, limitValue: number | null) => {
            const dto: UpdatePlanEntitlementRequestDto = {
                isEnabled: entitlement.isEnabled,
                limitValue,
            };
            try {
                await adminApi.updatePlanEntitlement(planId, entitlement.entitlementId, dto);
                await fetchEntitlements(planId);
            } catch {
                await fetchEntitlements(planId);
            }
        },
        [adminApi, fetchEntitlements],
    );

    const selectedPlan = plans.find((p) => p.id === selectedPlanId);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <LoadingIndicator size="lg" label="Loading plans..." />
            </div>
        );
    }

    if (error) {
        return <p className="py-6 text-sm text-error-primary">{error}</p>;
    }

    return (
        <div className="flex gap-6">
            {/* Plan list */}
            <div className="flex w-72 flex-shrink-0 flex-col gap-3">
                <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-secondary">Plans</span>
                    <Button size="sm" color="secondary" iconLeading={Plus} onClick={() => setShowCreateForm(true)}>
                        New
                    </Button>
                </div>

                {showCreateForm && (
                    <div className="flex flex-col gap-2 rounded-lg bg-secondary_subtle p-3 ring-1 ring-secondary">
                        <Input size="sm" placeholder="CODE" value={newCode} onChange={setNewCode} />
                        <Input size="sm" placeholder="Name" value={newName} onChange={setNewName} />
                        <Input size="sm" placeholder="Description (optional)" value={newDesc} onChange={setNewDesc} />
                        {createError && <p className="text-xs text-error-primary">{createError}</p>}
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                color="secondary"
                                onClick={() => {
                                    setShowCreateForm(false);
                                    setCreateError(null);
                                }}
                                isDisabled={isCreating}
                            >
                                Cancel
                            </Button>
                            <Button size="sm" color="primary" onClick={handleCreatePlan} isDisabled={isCreating || !newCode.trim() || !newName.trim()}>
                                {isCreating ? "Creating..." : "Create"}
                            </Button>
                        </div>
                    </div>
                )}

                <div className="flex flex-col gap-1">
                    {plans.map((plan) => (
                        <button
                            key={plan.id}
                            type="button"
                            onClick={() => handleSelectPlan(plan.id)}
                            className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left transition-colors ${selectedPlanId === plan.id ? "ring-brand-primary bg-secondary_alt text-primary ring-1" : "text-primary hover:bg-secondary_subtle"}`}
                        >
                            <div>
                                <div className="text-sm font-medium">{plan.name}</div>
                                <div className="font-mono text-xs text-tertiary">{plan.code}</div>
                            </div>
                            {!plan.isActive && (
                                <Badge color="gray" size="sm" type="pill-color">
                                    Inactive
                                </Badge>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Plan detail */}
            {selectedPlan ? (
                <div className="flex flex-1 flex-col gap-5">
                    {/* Plan fields */}
                    <div className="rounded-xl bg-primary p-5 shadow-xs ring-1 ring-secondary">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-primary">{selectedPlan.name}</h3>
                            {editingPlanId === selectedPlan.id ? (
                                <div className="flex gap-2">
                                    {savePlanError && <span className="text-xs text-error-primary">{savePlanError}</span>}
                                    <Button size="sm" color="secondary" onClick={() => setEditingPlanId(null)} isDisabled={isSavingPlan}>
                                        Cancel
                                    </Button>
                                    <Button size="sm" color="primary" onClick={() => savePlan(selectedPlan)} isDisabled={isSavingPlan || !editPlanName.trim()}>
                                        {isSavingPlan ? "Saving..." : "Save"}
                                    </Button>
                                </div>
                            ) : (
                                <Button size="sm" color="secondary" iconLeading={Edit05} onClick={() => startEditPlan(selectedPlan)}>
                                    Edit
                                </Button>
                            )}
                        </div>

                        {editingPlanId === selectedPlan.id ? (
                            <div className="flex flex-col gap-3">
                                <Input size="sm" label="Name" value={editPlanName} onChange={setEditPlanName} />
                                <Input size="sm" label="Description" value={editPlanDesc} onChange={setEditPlanDesc} />
                                <Toggle size="sm" label="Active" isSelected={editPlanActive} onChange={setEditPlanActive} />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1">
                                <div className="text-sm text-tertiary">{selectedPlan.description ?? <span className="italic">No description</span>}</div>
                                <div className="mt-1">
                                    <Badge color={selectedPlan.isActive ? "success" : "gray"} size="sm" type="pill-color">
                                        {selectedPlan.isActive ? "Active" : "Inactive"}
                                    </Badge>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Entitlements */}
                    <div className="rounded-xl bg-primary shadow-xs ring-1 ring-secondary">
                        <div className="border-b border-secondary px-5 py-3">
                            <h4 className="text-sm font-semibold text-primary">Entitlements</h4>
                        </div>

                        {entitlementsLoading ? (
                            <div className="flex items-center justify-center py-10">
                                <LoadingIndicator size="sm" label="Loading entitlements..." />
                            </div>
                        ) : entitlements.length === 0 ? (
                            <p className="px-5 py-6 text-sm text-tertiary">No entitlements found.</p>
                        ) : (
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-secondary bg-secondary_subtle">
                                        <th className="px-5 py-3 text-left text-xs font-medium text-tertiary">Code</th>
                                        <th className="px-5 py-3 text-left text-xs font-medium text-tertiary">Name</th>
                                        <th className="px-5 py-3 text-left text-xs font-medium text-tertiary">Category</th>
                                        <th className="px-5 py-3 text-center text-xs font-medium text-tertiary">Enabled</th>
                                        <th className="px-5 py-3 text-right text-xs font-medium text-tertiary">Limit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entitlements.map((ent) => (
                                        <tr key={ent.id} className="border-b border-secondary last:border-0">
                                            <td className="px-5 py-3">
                                                <span className="font-mono text-xs text-primary">{ent.entitlementCode}</span>
                                            </td>
                                            <td className="px-5 py-3 text-sm text-primary">{ent.entitlementName}</td>
                                            <td className="px-5 py-3 text-sm text-tertiary">{ent.entitlementCategory ?? "—"}</td>
                                            <td className="px-5 py-3 text-center">
                                                <Toggle
                                                    size="sm"
                                                    isSelected={ent.isEnabled}
                                                    onChange={(val) => handleEntitlementToggle(selectedPlan.id, ent, val)}
                                                />
                                            </td>
                                            <td className="px-5 py-3 text-right">
                                                <input
                                                    type="number"
                                                    className="focus:ring-brand-primary w-20 rounded border border-secondary bg-primary px-2 py-1 text-right text-sm text-primary focus:ring-1 focus:outline-none disabled:cursor-not-allowed disabled:bg-disabled_subtle disabled:text-disabled"
                                                    value={ent.limitValue ?? ""}
                                                    placeholder="—"
                                                    disabled={!ent.isEnabled}
                                                    onChange={(e) => {
                                                        const val = e.target.value === "" ? null : parseInt(e.target.value, 10);
                                                        if (val === null || !isNaN(val)) {
                                                            handleEntitlementLimit(selectedPlan.id, ent, val);
                                                        }
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-1 items-center justify-center py-20">
                    <p className="text-sm text-tertiary">Select a plan to view and manage its entitlements.</p>
                </div>
            )}
        </div>
    );
}

// =============================================================================
// BACKEND VERSION BADGE
// =============================================================================

function BackendVersionBadge() {
    const healthApi = useHealthApi();
    const [version, setVersion] = useState<BackendVersionDto | null>(null);

    useEffect(() => {
        let cancelled = false;
        healthApi
            .getBackendVersion()
            .then((v) => {
                if (!cancelled) setVersion(v);
            })
            .catch(() => {
                // Fail silent — version display must never break the admin page
            });
        return () => {
            cancelled = true;
        };
    }, [healthApi]);

    if (!version) return null;

    return (
        <span className="flex items-center gap-1.5 font-mono text-xs text-tertiary" title={`${version.informationalVersion} (${version.environment})`}>
            <Tag01 className="size-3.5" aria-hidden="true" />
            Backend v{version.version}
        </span>
    );
}

// =============================================================================
// ADMIN PAGE
// =============================================================================

export default function AdminPage() {
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [refreshClients, setRefreshClients] = useState(0);

    return (
        <AdminShell>
            <div className="mx-auto w-full max-w-[90rem] px-6 py-8">
                {/* Page Header */}
                <div className="mb-6 flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-display-xs font-semibold text-primary">Admin Console</h1>
                        <p className="mt-1 text-sm text-tertiary">Manage clients, settings, and platform configuration.</p>
                    </div>
                    <BackendVersionBadge />
                </div>

                <Tabs defaultSelectedKey="clients">
                    <TabList items={TABS} type="underline" size="sm">
                        {(item) => <Tab id={item.id}>{item.label}</Tab>}
                    </TabList>

                    <TabPanel id="clients" className="pt-6">
                        <ClientsSection key={refreshClients} onCreateOpen={() => setIsCreateOpen(true)} />
                    </TabPanel>

                    <TabPanel id="document-types" className="pt-6">
                        <div className="flex flex-col gap-10">
                            <div className="flex flex-col gap-4">
                                <h2 className="text-md font-semibold text-primary">Document Types</h2>
                                <DocumentTypesSection />
                            </div>
                            <div className="flex flex-col gap-4">
                                <h2 className="text-md font-semibold text-primary">Line Item Types</h2>
                                <LineItemTypesSection />
                            </div>
                        </div>
                    </TabPanel>

                    <TabPanel id="plans" className="pt-6">
                        <PlansSection />
                    </TabPanel>

                    <TabPanel id="api-key" className="pt-6">
                        <MailboxWorkerKeyTab />
                    </TabPanel>
                </Tabs>
            </div>

            <CreateClientModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} onCreated={() => setRefreshClients((n) => n + 1)} />
        </AdminShell>
    );
}
